//port of chatmeter/dashboard-play/ui/app/modules/reviews/reviewResponseConfiguration.js
import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import providerStore from '../providers/ProvidersStore';
import sessionStore from '../session/SessionStore';
import selectedLocationStore from '../locations/SelectedLocationsStore';
import brandConfigStore from '../config/BrandConfigStore';
import listingsAPI from '../listings/api';


//TODO - is this a service, more of a helper
class ReviewResponseConfiguration {
  constructor() {
    makeObservable(this, {
      respondableListings: observable,
      isLoading: observable,
      loadRespondableListings: action,
      responseConfiguration: computed
    });
  }

  static configWithDefaults(sparse) {
    return {
      requiresProviderReviewId: false, // whether we require the review.providerReviewId field in order to respond
      canRespond: false, // whether the user / account has the required configuration for responding to this provider
      canCreateDraft: false, // whether the user has permission to create a response draft
      canRespondBulk: true, // whether the review can be responded to as part of a multi response
      canRespondBulkFalseyMessage: undefined, // A message to display over the multi select checkbox if the user cannot respond in bulk
      imitateResponsesForDemo: false, // whether the component fake a response despite canRespond for demo purposes
      usesCredentials: false, // whether this provider requires user entered credentials for responding. Usually goes hand in hand with the following 2
      createCredentials: false, // whether this provider can have credentials added. This is split from the above because YELP != YELPCA, and YELPCA just reuses the YELP credentials.
      requiresRespondableListing: false, // whether the review's listing must be associated with a credential to respond
      shouldShowForm: false, // whether the response form should be shown (even if the provider is completely unsupported). I guess we want this a teaser? seems kind of annoying to me
      isReviewBuilder: false, // whether this is a review builder provider and should be treated as less public
      unsupported: false, // whether this provider has an integration (despite the accounts configuration)
      ...sparse
    }
  }

  static toConfigForProvider(provider, user, brandConfig) {
    const p = provider && provider.id;
    let config;
    const isAccountDisabled = brandConfig.restrictions.hideReviewResponses;
    const isDemo = !brandConfig.restrictions.hideDemoReviewResponses;
    const hasApprovePermission = user.permissions.includes('ApproveReviewResponse');
    const hasDraftPermission = user.permissions.includes('SubmitReviewResponse');

    if (p === 'FACEBOOK') {
      config = ReviewResponseConfiguration.configWithDefaults({
        canRespond: hasApprovePermission && !isAccountDisabled,
        canCreateDraft: hasDraftPermission && !isAccountDisabled,
        imitateResponsesForDemo: isDemo,
        usesCredentials: true,
        createCredentials: true,
        requiresRespondableListing: true,
      });
    } else if (p === 'GOOGLE') {
      config = ReviewResponseConfiguration.configWithDefaults({
        canRespond: hasApprovePermission && !isAccountDisabled,
        canCreateDraft: hasDraftPermission && !isAccountDisabled,
        imitateResponsesForDemo: isDemo,
        usesCredentials: true,
        createCredentials: true,
        requiresRespondableListing: true,
        requiresProviderReviewId: !isDemo
      });
    } else if (provider && provider.providerSettings.isReviewBuilder) {
      config = ReviewResponseConfiguration.configWithDefaults({
        canRespond: hasApprovePermission,
        canCreateDraft: true,
        isReviewBuilder: true
      });
    } else if (p === 'APARTMENTGUIDE' || p === 'RENT') {
      config = ReviewResponseConfiguration.configWithDefaults({
        canRespond: !isAccountDisabled && hasApprovePermission && !brandConfig.restrictions.hideApartmentGuideReviewResponses,
        canCreateDraft: !isAccountDisabled && hasDraftPermission && !brandConfig.restrictions.hideApartmentGuideReviewResponses,
        imitateResponsesForDemo: isDemo,
      });
    } else if (p === 'GOLFADVISOR') {
      config = ReviewResponseConfiguration.configWithDefaults({
        canRespond: !isAccountDisabled && hasApprovePermission && !brandConfig.restrictions.hideGolfAdvisorReviewResponses,
        canCreateDraft: !isAccountDisabled && hasDraftPermission && !brandConfig.restrictions.hideGolfAdvisorReviewResponses,
        imitateResponsesForDemo: isDemo,
        unsupported: false
      });
    } else if (p === 'CARS') {
      config = ReviewResponseConfiguration.configWithDefaults({
        canRespond: !isAccountDisabled && hasApprovePermission,
        canCreateDraft: !isAccountDisabled && hasDraftPermission,
        imitateResponsesForDemo: isDemo,
      });
    } else if (['YELP', 'YELPCA', 'YELPGB'].includes(p)) {
      const yelpEnabled = !brandConfig.restrictions.hideYelpResponding;
      config = ReviewResponseConfiguration.configWithDefaults({
        canRespond: hasApprovePermission && (yelpEnabled || isDemo),
        canCreateDraft: hasDraftPermission && (yelpEnabled || isDemo),
        canRespondBulk: false, // Yelp does not allow bulk responses
        canRespondBulkFalseyMessage: "Bulk responding is not available for Yelp reviews.",
        imitateResponsesForDemo: false, //isDemo, TODO - finish yelp integration
        usesCredentials: yelpEnabled,
        createCredentials: (p === 'YELP') && yelpEnabled,
        requiresRespondableListing: yelpEnabled,
        unsupported: true // TODO - finish yelp integration
      });
    } else if (p === "TRUSTPILOT") {
      config = ReviewResponseConfiguration.configWithDefaults({
        createCredentials: true,
        requiresProviderReviewId: true,
        requiresRespondableListing: true,
        canRespond: hasApprovePermission && !isAccountDisabled
      });
    } else {
      config = ReviewResponseConfiguration.configWithDefaults({ unsupported: true });
    }


    return {
      ...config,
      provider: provider,
      shouldShowForm: ((hasApprovePermission || hasDraftPermission) && !isAccountDisabled) || config.canRespond || config.canCreateDraft
    };
  }

  respondableListings = [];

  isLoading = false;


  loadRespondableListings() {
    this.isLoading = true;
    return listingsAPI.getRespondableListingIds(selectedLocationStore.legacyGroupIdOrAll).then(listings => runInAction(() => {
      this.isLoading = false;
      this.respondableListings = listings;
    }));
  }


  //TODO maybe better caching than this
  //TODO - invalidate on logout
  get responseConfiguration() {
    let user = sessionStore.user;
    let providers = providerStore.reviewResponseProviders;
    let brandConfig = brandConfigStore.brandConfig;

    return providers.concat([undefined]).reduce((sum, p) => {
      const config = ReviewResponseConfiguration.toConfigForProvider(p, user, brandConfig);
      return {
        ...sum,
        [p && p.id || '__DEFAULT__']: config
      }
    }, {})
  }

  listReviewResponseCredentialProviders() {
    return Object.values(this.responseConfiguration).filter(x => {
      return x.createCredentials && !!x.provider
    }).map(x => x.provider)
  }

  forProvider(provider){
    const config = this.responseConfiguration;
    if(!config){
      return ReviewResponseConfiguration.configWithDefaults({})
    }else{
      return this.responseConfiguration[provider] || this.responseConfiguration['__DEFAULT__']
    }
  }

  isResponseAllowed(review) {
    const config = this.forProvider(review.contentProvider);

    return config.canRespond &&
      (
        !config.requiresRespondableListing ||
        this.respondableListings.includes(review.listingId) ||
          config.imitateResponsesForDemo
      ) &&
      !(config.requiresProviderReviewId && review.providerReviewId === undefined);

  }

  isDraftResponseAllowed(review) {
    const config = this.forProvider(review.contentProvider)
    return config.canCreateDraft && // user / provider has the permission
      (
        !config.requiresRespondableListing || // has credentials
        this.respondableListings.includes(review.listingId) ||
        config.imitateResponsesForDemo
      ) &&
      !(config.requiresProviderReviewId && review.providerReviewId === undefined);
  }

  isBulkResponseAllowed(review){
    return this.forProvider(review.contentProvider).canRespondBulk;
  }
}

export default new ReviewResponseConfiguration();